import { Injectable, Signal, inject, signal } from '@angular/core';
import { IS_MOBILE } from '@lite-workspace/utils';
import type { Playlist } from '../catalog.types';
import { ONBOARDING_MEDIA_BASE } from './onboarding-media.base';

/** Запись манифеста: длительность для карточки урока и версия файлов. */
export interface OnboardingMediaEntry {
  duration: string;
  rev: string;
  /** Рядом лежит `<id>-cover.jpg` — своя обложка карточки подсказки. */
  cover?: true;
  /** Рядом лежит вертикальная пара для телефона: `<id>-mobile-1080.mp4` и `<id>-mobile.jpg`. */
  mobile?: true;
  mobileDuration?: string;
}

export type OnboardingMediaManifest = Record<string, OnboardingMediaEntry>;

/** Ролик и обложка урока под постоянными именами. */
export interface OnboardingMedia {
  clip: string;
  poster: string;
  /** Обложка карточки подсказки; `null` — у урока своей нет, карточка берёт общую. */
  cover: string | null;
}

/**
 * Публичная папка вне нашего API: `fetch` мимо перехватчиков, чтобы не слать туда токен. Файлы
 * перезаписываются под теми же именами, поэтому читаем их мимо кэша. Файла нет или он битый — уроки
 * остаются с заглушкой, ошибки на экране быть не должно.
 */
function read<T>(file: string): Promise<T | null> {
  return fetch(`${ONBOARDING_MEDIA_BASE}/${file}`, { cache: 'no-store' })
    .then(response => (response.ok ? (response.json() as Promise<T>) : null))
    .catch((): null => null);
}

/**
 * Медиа урока адресуется по имени подсказки: `<id>-1080.mp4` для любого экрана и `<id>.jpg`. Какие уроки
 * сняты, сколько идут и какой версии — один `manifest.json`, читаемый раз за сессию. Файл в хранилище
 * перезаписывается под тем же именем, поэтому каждая ссылка несёт `?v=<rev>`. Рядом лежит
 * `playlist.json` — разделы, порядок и тексты уроков окна, они приезжают из спеки без релиза.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingMediaService {
  private readonly isMobile = inject(IS_MOBILE);
  private readonly entries = signal<OnboardingMediaManifest>({});
  private readonly lessons = signal<Playlist | null>(null);

  /** Плейлист окна уроков; `null` — ещё не загружен или не доехал. */
  readonly playlist: Signal<Playlist | null> = this.lessons.asReadonly();

  constructor() {
    if (ONBOARDING_MEDIA_BASE) {
      // Файла нет или он битый — остаётся то, что уже стоит: витрина свои манифест и плейлист ставит сама.
      read<OnboardingMediaManifest>('manifest.json').then(manifest => manifest && this.entries.set(manifest));
      read<Playlist>('playlist.json').then(playlist => playlist && this.lessons.set(playlist));
    }
  }

  /** Ролик урока снят и лежит в папке: манифест знает его id. Манифеста ещё нет — не снят. */
  has(id: string): boolean {
    return !!this.entries()[id];
  }

  /** Телефон и у урока есть вертикальная пара — берём её. */
  private vertical(entry: OnboardingMediaEntry | undefined): boolean {
    return !!entry?.mobile && this.isMobile();
  }

  /** Длительность урока из манифеста; пусто — ролика ещё нет. */
  duration(id: string): string {
    const entry = this.entries()[id];

    return (this.vertical(entry) ? entry?.mobileDuration : entry?.duration) ?? '';
  }

  /** Файлы урока: ролик `-1080` на любом экране, на телефоне — вертикальный, если он снят. `null` — заглушка. */
  media(id: string): OnboardingMedia | null {
    const entry = ONBOARDING_MEDIA_BASE ? this.entries()[id] : undefined;
    if (!entry) return null;

    const version = `?v=${entry.rev}`;
    const name = this.vertical(entry) ? `${id}-mobile` : id;

    return {
      clip: `${ONBOARDING_MEDIA_BASE}/${name}-1080.mp4${version}`,
      poster: `${ONBOARDING_MEDIA_BASE}/${name}.jpg${version}`,
      cover: entry.cover ? `${ONBOARDING_MEDIA_BASE}/${id}-cover.jpg${version}` : null,
    };
  }

  /** Витрина каталога компонентов подставляет свой манифест: файлов в хранилище у неё нет. */
  setManifest(manifest: OnboardingMediaManifest): void {
    this.entries.set(manifest);
  }

  /** Витрина подставляет и свой плейлист: хранилища с уроками у неё тоже нет. */
  setPlaylist(playlist: Playlist | null): void {
    this.lessons.set(playlist);
  }
}
